import * as Haptics from 'expo-haptics';
import { useState, useCallback } from 'react';

import { useData } from '@/components/data-provider';
import { useDeviceInfo } from '@/hooks/use-device-info';

/**
 * Hook for handling pull-to-refresh with API refetch
 * Enhanced with iPad-specific optimizations
 *
 * @returns Refresh state and handlers for pull-to-refresh functionality
 */
export function usePullToRefresh() {
	const [refreshing, setRefreshing] = useState(false);
	const { refetchAll } = useData();
	const { isTablet, platform } = useDeviceInfo();

	const onRefresh = useCallback(async () => {
		// Provide haptic feedback (only on iOS devices that support it)
		if (platform === 'ios') {
			try {
				await Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
			} catch (error) {
				// Haptics might not be available on some devices
				console.warn('Haptics not available:', error);
			}
		}

		// Start refreshing
		setRefreshing(true);

		try {
			// Refetch all queries
			refetchAll();
			console.log('Pull to refresh completed successfully');
		} catch (error) {
			console.error('Error during pull to refresh:', error);
		} finally {
			// End refreshing
			setRefreshing(false);
		}
	}, [refetchAll, platform]);

	/**
	 * Get iPad-optimized RefreshControl props
	 * iPad requires different touch sensitivity and visual feedback
	 */
	const getRefreshControlProps = () => {
		const baseProps = {
			refreshing,
			onRefresh,
		};

		// iPad-specific optimizations
		if (isTablet && platform === 'ios') {
			return {
				...baseProps,
				// Increase progress view offset for better iPad visibility
				progressViewOffset: 20,
				// Use a more visible background color for iPad
				progressBackgroundColor: '#f0f0f0',
				// Ensure proper tint color visibility on iPad
				tintColor: '#007AFF',
				// Add colors array for better cross-platform support
				colors: ['#007AFF'],
			};
		}

		// Default props for other devices
		return baseProps;
	};

	return {
		refreshing,
		onRefresh,
		getRefreshControlProps,
		isTablet,
	};
}
